import { useState, useEffect } from 'react'
import {
  Card,
  Col,
  Container,
  Alert,
  Row,
  Breadcrumb,
  Button,
} from 'react-bootstrap'
import { useParams, useLocation } from 'react-router-dom'
import CopyToClipboardButton from 'components/copy-to-clipboard-button'
import StripeProductTable from 'components/stripe-products-table'
import {
  AdjustProductQuantity,
  AdjustSeatCountCheckBox,
  HubspotDealLink,
  LinqCouponId,
  LinqOneDiscountInput,
  StripeSelect,
  TrialCheckBox,
  CollectShippingAddress,
  AllowCustomerUseReferralCode,
} from 'components/stripe-link-builder-components'
import {
  useAdminStripeAvailablePrices,
  useAdminStripeAvailableProducts,
} from 'hooks/use-admin-stripe'
import { useCreateStripeCheckoutLink } from 'hooks/use-admin-stripe-checkout-links'
import { useGetInternalAdmins } from 'hooks/use-get_internal-admins'
import { useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { LineItem } from 'interfaces/line-item'
import { User } from 'interfaces/user'
import { RootState } from 'index'
import { StripeProduct } from 'interfaces/stripe-product'
import { LINQ_ONE_DISCOUNTS } from 'constants/linq-one-discount'
import useValidateHubspotDeal from 'hooks/use-validate-hubspot-deal'

const LINQ_ONE_PRODUCT_ID = 'prod_QmwEcZEeTODmrW'
const LINQ_BLUE_PRODUCT_IDS = ['prod_RQjmrtiHgXI5PS']
const LINQ_BLUE_INFRASTRUCTURE_PRODUCT_IDS = [
  'prod_TI8X1aY7VApKAs',
  'prod_V0ouQMnfLdZU3s',
]

const SubscriptionLinkBuilderPage = () => {
  const { dealid } = useParams<{ dealid?: string }>()
  const location = useLocation()
  const currentUser = useSelector<RootState, User | null>(
    (state) => state.user.currentUser
  )

  // Extract dealName from query params
  const queryParams = new URLSearchParams(location.search)
  const dealNameFromUrl = queryParams.get('dealName')
  const [couponId, setCouponId] = useState<string | undefined>('')
  const [linqOneDiscount, setLinqOneDiscount] = useState<
    string | number | undefined
  >(undefined)
  const [saleRepId, setSaleRepId] = useState<string | number | undefined>(
    currentUser?.id
  )
  const [productId, setProductId] = useState<string | number | undefined>('')
  const [productPriceId, setProductPriceId] = useState<
    string | number | undefined
  >('')
  const [productQuantity, setProductQuantity] = useState(1)
  const [allowQuantityAdjusting, setAllowQuantityAdjusting] = useState(false)
  const [hasTrial, setHasTrial] = useState(false)
  const [collectShippingAddress, setCollectShippingAddress] = useState(true)
  const [addedProductsList, setAddedProductsList] = useState<
    Partial<StripeProduct>[]
  >([])
  const [savedLineItems, setSavedLineItems] = useState<Partial<LineItem>[]>([])
  const [checkoutLink, setCheckoutLink] = useState<string | undefined>(
    undefined
  )
  const [hubspotDealLink, setHubspotDealLink] = useState<string>('')
  const [hubspotValidationResult, setHubspotValidationResult] = useState<{
    isValid: boolean
    error?: string
    message?: string
  } | null>(null)
  const [allowCustomerUseReferralCode, setAllowCustomerUseReferralCode] =
    useState(false)
  const {
    data: availableProducts,
    isLoading: isProductsLoading,
    refetch: refectchAvailableProducts,
    isFetching: productsIsFetching,
  } = useAdminStripeAvailableProducts()
  const activeProducts = availableProducts?.filter(
    (product) => !product.name.toLowerCase().includes('hyper')
  )
  const {
    data: productPrices,
    isLoading: isProductPricesLoading,
    refetch: refetchProductPrices,
    isFetching: productPricesIsFetching,
  } = useAdminStripeAvailablePrices(productId || '')

  const { mutate: stripeCheckoutLink, isLoading: stripeCheckoutLinkLoading } =
    useCreateStripeCheckoutLink()

  const { data: internalAdmins, isFetching: isInternalAdminFetchiing } =
    useGetInternalAdmins()

  const { mutateAsync: validateDeal, isLoading: isValidatingHubspotLink } =
    useValidateHubspotDeal()

  // Pre-fill HubSpot deal link and trigger validation when dealid is in URL
  useEffect(() => {
    if (dealid && !hubspotDealLink) {
      const formattedLink = `https://app.hubspot.com/contacts/7157564/record/0-3/${dealid}`
      setHubspotDealLink(formattedLink)

      // Auto-validate in background, especially when dealName is present
      setTimeout(async () => {
        try {
          await validateDeal({ dealId: dealid })
          setHubspotValidationResult({
            isValid: true,
          })
        } catch (error: any) {
          const errorMessage =
            error.message || 'Please enter a valid Hubspot Deal link.'

          if (errorMessage.toLowerCase().includes('deal id already exist')) {
            setHubspotValidationResult({
              isValid: true,
              message:
                'Subscription already created on this deal. Please check the deal link in Hubspot.',
            })
          } else if (dealNameFromUrl) {
            // Even if validation fails, if dealName is present, mark as valid for UI purposes
            setHubspotValidationResult({ isValid: true })
          } else {
            setHubspotValidationResult({
              isValid: false,
              error: errorMessage,
            })
          }
        }
      }, 100)
    }
  }, [dealid, dealNameFromUrl])

  const handleValidateHubspotDeal = async () => {
    if (!hubspotDealLink.trim()) {
      setHubspotValidationResult({
        isValid: false,
        error: 'Hubspot Deal Link is required.',
      })
      return
    }

    const basicUrlPattern = /^(https?:\/\/)?app\.hubspot\.com\//
    if (!basicUrlPattern.test(hubspotDealLink)) {
      setHubspotValidationResult({
        isValid: false,
        error: 'Link must be a valid Hubspot Deal URL.',
      })
      return
    }

    const extracted = extractHubspotId(hubspotDealLink)
    if (extracted.type !== 'deal' || !extracted.id) {
      setHubspotValidationResult({
        isValid: false,
        error: 'Please enter a valid Hubspot Deal link format.',
      })
      return
    }

    try {
      await validateDeal({ dealId: extracted.id })
      setHubspotValidationResult({
        isValid: true,
      })
    } catch (error: any) {
      const errorMessage =
        error.message || 'Please enter a valid Hubspot Deal link.'

      if (errorMessage.toLowerCase().includes('deal id already exist')) {
        setHubspotValidationResult({
          isValid: true,
          message:
            'Subscription already created on this deal. Please check the deal link in Hubspot.',
        })
      } else {
        setHubspotValidationResult({
          isValid: false,
          error: errorMessage,
        })
      }
    }
  }

  const handleNewCouponClick = () => {
    window.open('https://dashboard.stripe.com/coupons', '_blank')
  }

  const handleAddProducts = (
    productId: number | string | undefined,
    productPriceId: number | string | undefined,
    productQuantity: number
  ) => {
    if (productQuantity < 1) {
      window.alert('Quantity must be greater than 0')
      return
    }
    if (productId && productPriceId) {
      const selectedProduct = availableProducts?.find(
        (product) => product.id === productId
      )
      const selectedPrice = productPrices?.find(
        (price) => price.id === productPriceId
      )

      let changeWithOrgSeatCount
      if (selectedPrice?.metadata?.change_with_org_seat_count) {
        changeWithOrgSeatCount =
          selectedPrice?.metadata?.change_with_org_seat_count
      } else if (selectedProduct?.metadata?.change_with_org_seat_count) {
        changeWithOrgSeatCount =
          selectedProduct?.metadata?.change_with_org_seat_count
      }

      setAddedProductsList([
        ...addedProductsList,
        {
          name: selectedProduct?.name,
          id: selectedProduct?.id,
          price: selectedPrice?.unit_amount
            ? Number(selectedPrice?.unit_amount / 100).toFixed(2)
            : undefined,
          description: selectedPrice?.metadata?.internal_description,
          metadata: {
            change_with_org_seat_count: changeWithOrgSeatCount,
          },
          quantity: productQuantity,
          isAdjustableQuantity: allowQuantityAdjusting,
        },
      ])
      setSavedLineItems([
        ...savedLineItems,
        {
          price: productPriceId,
          product_id: productId,
          quantity: productQuantity,
          adjustable_quantity: {
            enabled: allowQuantityAdjusting && productQuantity < 999,
            minimum: allowQuantityAdjusting && productQuantity < 999 ? 1 : null,
            maximum:
              allowQuantityAdjusting && productQuantity < 999 ? 999 : null,
          },
        },
      ])
    } else {
      toast('Both Product and Price must be selected', { icon: '❌' })
    }
  }

  const handleRemoveProduct = (index: number) => {
    const updatedProductList = addedProductsList.filter(
      (_, idx) => idx !== index
    )
    setAddedProductsList(updatedProductList)
    const updateSavedLineItems = savedLineItems.filter(
      (_, idx) => idx !== index
    )
    setSavedLineItems(updateSavedLineItems)
  }

  const extractHubspotId = (
    link: string
  ): { type: 'deal' | null; id: string | null } => {
    if (!link) return { type: null, id: null }
    try {
      const url = new URL(link.trim())
      const pathParts = url.pathname.split('/').filter((part) => part !== '')
      let id: string | null = null

      const findIdAfterSegment = (segment: string): string | null => {
        try {
          const index = pathParts.indexOf(segment)
          if (index !== -1 && index + 1 < pathParts.length) {
            const potentialId = pathParts[index + 1].split('?')[0]
            if (/^\d+$/.test(potentialId)) {
              return potentialId
            }
          }
        } catch (e) {
          console.error('Error parsing Hubspot link:', e)
        }
        return null
      }

      id = findIdAfterSegment('0-3')
      if (id) return { type: 'deal', id: id }

      id = findIdAfterSegment('deal')
      if (id) return { type: 'deal', id: id }

      return { type: null, id: null }
    } catch (error) {
      return { type: null, id: null }
    }
  }

  const handleSaveLineItems = async () => {
    const subscriptionProductIds = [
      LINQ_ONE_PRODUCT_ID,
      ...LINQ_BLUE_INFRASTRUCTURE_PRODUCT_IDS,
      ...LINQ_BLUE_PRODUCT_IDS,
      process.env.REACT_APP_STRIPE_LINQ_FOR_TEAM_PRODUCT_ID,
    ]

    const hasSubscriptionProduct = addedProductsList?.some((product) =>
      subscriptionProductIds.includes(product.id)
    )

    if (!hasSubscriptionProduct) {
      toast(
        'HEY, YOU MUST ADD ONE SUBSCRIPTION ITEM LIKE: LFT, Linq One, or Linq Blue or Linq Blue Infrastructure.',
        { icon: `🚨` }
      )
      return
    }

    if (!saleRepId) {
      toast.error('Please select a Sale Representative.')
      return
    }

    if (!hubspotDealLink) {
      toast.error('Hubspot Deal Link is required.')
      return
    }

    const linqOneCouponId = LINQ_ONE_DISCOUNTS.find(
      (coupon) => coupon.percent_off === linqOneDiscount
    )?.id
    const hasLinqOneProduct = addedProductsList?.some(
      (product) => product.id === LINQ_ONE_PRODUCT_ID
    )
    const coupon = allowCustomerUseReferralCode
      ? undefined
      : hasLinqOneProduct
      ? linqOneCouponId
      : couponId

    const hasLinqBlueProduct = addedProductsList?.some(
      (product) => product.id && LINQ_BLUE_PRODUCT_IDS.includes(product.id)
    )

    if (hasTrial) {
      if (hasLinqBlueProduct) {
        window.alert('Trial for Linq Blue is Not Allowed')
        return
      }

      const userConfirmedTrial = window.confirm(
        'This is a trial. Are you sure?'
      )
      if (!userConfirmedTrial) {
        return
      }
    }

    stripeCheckoutLink(
      {
        lineItems: savedLineItems,
        couponId: coupon,
        saleRepId: saleRepId,
        hasTrial: hasTrial,
        hubspotDealLink: hubspotDealLink,
        mode: 'subscription',
        collectShippingAddress: collectShippingAddress,
        allowCustomerUseReferralCode: allowCustomerUseReferralCode,
      },
      {
        onSuccess: (data) => {
          setCheckoutLink(data.link)
        },
      }
    )
  }

  const clearProductFormFields = () => {
    setProductId('')
    setProductPriceId('')
    setProductQuantity(0)
    setAllowQuantityAdjusting(false)
    toast('Fields cleared', { icon: `🚀` })
  }

  const handleAllowQuantityAdjusting = (value: boolean) => {
    if (value) {
      const userConfirmed = window.confirm(
        'Are you sure you want to allow user to adjust quantity? This will allow user to decrease/increase the quantity of the product.'
      )
      if (userConfirmed) {
        setAllowQuantityAdjusting(value)
      }
    } else {
      setAllowQuantityAdjusting(value)
    }
  }

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item active href="/subscription-purchase-link">
          Subscription Link Builder
        </Breadcrumb.Item>
      </Breadcrumb>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 style={{ fontSize: '28px', fontWeight: 700, margin: 0 }}>
          Subscription Link Builder
        </h1>
      </div>

      <Card className="mt-4 mb-4 p-2 text-white bg-dark">
        <Card.Body>
          <Row>
            <Col lg={3}>
              <StripeSelect
                id="products"
                label="products"
                labelDescription="Linq Products"
                nullValue="Select a product..."
                value={productId}
                setState={setProductId}
                data={activeProducts}
                refetch={refectchAvailableProducts}
                showRefetchButton={!isProductsLoading}
                isFetching={productsIsFetching}
              />
              <StripeSelect
                id="productPrice"
                label="productPrice"
                labelDescription="Prices"
                nullValue="Select a price..."
                value={productPriceId}
                setState={setProductPriceId}
                data={productPrices}
                refetch={refetchProductPrices}
                showRefetchButton={Boolean(
                  productId && !isProductPricesLoading
                )}
                isFetching={productPricesIsFetching}
              />
              {(productId === LINQ_ONE_PRODUCT_ID || linqOneDiscount) &&
                !allowCustomerUseReferralCode && (
                  <LinqOneDiscountInput
                    linqOneDiscount={linqOneDiscount}
                    setLinqOneDiscount={setLinqOneDiscount}
                  />
                )}
              <AdjustProductQuantity
                productQuantity={productQuantity}
                setProductQuantity={setProductQuantity}
                allowQuantityAdjusting={allowQuantityAdjusting}
                setAllowQuantityAdjusting={setAllowQuantityAdjusting}
              />
              <AdjustSeatCountCheckBox
                productQuantity={productQuantity}
                allowQuantityAdjusting={allowQuantityAdjusting}
                handleAllowQuantityAdjusting={handleAllowQuantityAdjusting}
              />
              <TrialCheckBox hasTrial={hasTrial} setHasTrial={setHasTrial} />
              <AllowCustomerUseReferralCode
                allowCustomerUseReferralCode={allowCustomerUseReferralCode}
                setAllowCustomerUseReferralCode={
                  setAllowCustomerUseReferralCode
                }
              />
              <CollectShippingAddress
                collectShippingAddress={collectShippingAddress}
                setCollectShippingAddress={setCollectShippingAddress}
              />
              <div style={{ display: 'flex' }}>
                <Button
                  style={{ marginRight: '0.5rem', flexGrow: 1 }}
                  className="mt-2"
                  onClick={() =>
                    handleAddProducts(
                      productId,
                      productPriceId,
                      productQuantity
                    )
                  }
                  variant="success"
                >
                  + Add Product
                </Button>
                <Button className="mt-2" onClick={clearProductFormFields}>
                  Clear Fields
                </Button>
              </div>
            </Col>
            <Col lg={9}>
              <div className="form-floating mb-4">
                <h4>Added Products</h4>
                <Alert variant="secondary">
                  <StripeProductTable
                    addedProductsList={addedProductsList}
                    handleRemoveProduct={handleRemoveProduct}
                  />
                </Alert>
              </div>
            </Col>
          </Row>

          {productId !== LINQ_ONE_PRODUCT_ID &&
            !linqOneDiscount &&
            !allowCustomerUseReferralCode && (
              <div className="d-flex flex-row align-items-center">
                <LinqCouponId couponId={couponId} setCouponId={setCouponId} />
                <Button onClick={handleNewCouponClick} className="m-4">
                  Stripe Coupons <ion-icon name="open-outline" />
                </Button>
              </div>
            )}

          <HubspotDealLink
            hubspotDealLink={hubspotDealLink}
            setHubspotDealLink={setHubspotDealLink}
            onValidate={handleValidateHubspotDeal}
            isValidating={isValidatingHubspotLink}
            validationResult={hubspotValidationResult}
            onInputChange={() => setHubspotValidationResult(null)}
            disabled={!!dealid}
            dealName={dealNameFromUrl || undefined}
          />

          <StripeSelect
            id="saleReps"
            label="saleReps"
            labelDescription="Sale Representative"
            nullValue="Select a sale rep..."
            isFetching={isInternalAdminFetchiing}
            value={saleRepId}
            setState={setSaleRepId}
            data={internalAdmins as any}
          />
          <Button
            className="mt-2 mb-2"
            onClick={handleSaveLineItems}
            variant="success"
            size="lg"
            disabled={
              isValidatingHubspotLink ||
              stripeCheckoutLinkLoading ||
              !hubspotValidationResult?.isValid
            }
          >
            {stripeCheckoutLinkLoading ? 'Generating Link...' : 'Generate Link'}
          </Button>
          <Alert variant="secondary">
            {stripeCheckoutLinkLoading && <div>Loading...</div>}

            {checkoutLink && (
              <>
                <div className="mb-1">{checkoutLink}</div>
                <CopyToClipboardButton
                  copyText="Copy Link"
                  copiedText="Link Copied to Clipboard"
                  stringToCopy={checkoutLink}
                />
              </>
            )}
          </Alert>
        </Card.Body>
      </Card>
    </Container>
  )
}

export default SubscriptionLinkBuilderPage
